import { Box, styled, Typography } from '@mui/material';
import { IconEdit, IconTrash } from 'assets/img';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LwButton } from 'redesign';
import { MultiDayDayBlockItemProps } from './multi-day-day-block-item.types';
import { MultiDayDayBlockPreviewFields } from './multi-day-day-block-preview-fields';
import { MultiDayDayBlockTimeInputs } from './multi-day-day-block-time-inputs';
import {
  formatMultiDayBlockHeaderDate,
  getMultiDayDayBlockBreakLabel,
  getMultiDayDayBlockFieldPath,
} from '../helpers/multi-day-day-block.helpers';
import { MultiDayWorkFormContext } from '../multi-day-work.types';

export const MultiDayDayBlockItem = ({
  breakOptions,
  index,
  isEditing,
  onCloseEdit,
  onDelete,
  onEdit,
}: MultiDayDayBlockItemProps) => {
  const { t } = useTranslation('web-common');
  const { watch } = useFormContext<MultiDayWorkFormContext>();

  const dateFieldPath = getMultiDayDayBlockFieldPath(index, 'date');
  const startTimeFieldPath = getMultiDayDayBlockFieldPath(index, 'startTime');
  const endTimeFieldPath = getMultiDayDayBlockFieldPath(index, 'endTime');
  const breakMinutesFieldPath = getMultiDayDayBlockFieldPath(index, 'breakMinutes');

  const date = watch(dateFieldPath) ?? '';
  const startTime = watch(startTimeFieldPath) ?? '';
  const endTime = watch(endTimeFieldPath) ?? '';
  const breakMinutes = watch(breakMinutesFieldPath);

  const dayLabel = t('work_form.multi_day.day_blocks.title', { dayNumber: index + 1 });
  const headerDate = date ? formatMultiDayBlockHeaderDate(date) : '';
  const breakLabel = getMultiDayDayBlockBreakLabel(
    breakMinutes == null ? undefined : String(breakMinutes),
    breakOptions
  );
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (!isEditing || event.key !== 'Escape') {
        return;
      }

      onCloseEdit();
    },
    [isEditing, onCloseEdit]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [handleEscape]);

  return (
    <DayBlockCard data-testid={`multi-day-day-block-${index}`}>
      <HeaderRow>
        <TitleGroup>
          <DayIndex variant="body1">{dayLabel}</DayIndex>
          {headerDate ? <HeaderDate variant="body1">{headerDate}</HeaderDate> : null}
        </TitleGroup>
        <HeaderActions>
          {isEditing ? (
            <DeleteButton
              color="tertiary"
              data-testid={`multi-day-day-block-${index}-delete`}
              iconColorMode="stroke"
              onClick={onDelete}
              startIcon={<IconTrash width={18} height={18} />}
              type="button"
            >
              {t('buttons.delete')}
            </DeleteButton>
          ) : (
            <>
              <EditButton
                color="tertiary"
                data-testid={`multi-day-day-block-${index}-edit`}
                iconColorMode="fill"
                onClick={onEdit}
                startIcon={<IconEdit width={18} height={18} />}
                type="button"
              >
                {t('work_form.multi_day.day_blocks.edit')}
              </EditButton>
            </>
          )}
        </HeaderActions>
      </HeaderRow>
      {isEditing ? (
        <MultiDayDayBlockTimeInputs breakOptions={breakOptions} index={index} />
      ) : (
        <MultiDayDayBlockPreviewFields
          breakLabel={breakLabel}
          endTime={endTime}
          index={index}
          startTime={startTime}
        />
      )}
    </DayBlockCard>
  );
};

const DayBlockCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(4),
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(3),
  justifyContent: 'space-between',
}));

const TitleGroup = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  flexWrap: 'nowrap',
  gap: theme.spacing(2),
  minWidth: 0,
}));

const DayIndex = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  flexShrink: 0,
  whiteSpace: 'nowrap',
}));

const HeaderDate = styled(Typography)({
  flexShrink: 0,
  margin: 0,
  whiteSpace: 'nowrap',
});

const HeaderActions = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  gap: theme.spacing(2),
}));

const borderlessTertiaryButtonStyles = {
  '&.MuiButton-containedTertiary': {
    backgroundColor: 'transparent',
    border: 'none',
    minHeight: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
      border: 'none',
      boxShadow: 'none',
    },
    '&:focus': {
      backgroundColor: 'transparent',
      border: 'none',
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: 'transparent',
      border: 'none',
    },
  },
};

const EditButton = styled(LwButton)(({ theme }) => ({
  color: theme.palette.lwPrimaryAccentDark[100],
  ...borderlessTertiaryButtonStyles,
  '& .MuiButton-startIcon svg path': {
    fill: 'currentColor',
    stroke: 'none',
  },
}));

const DeleteButton = styled(LwButton)(({ theme }) => ({
  color: theme.palette.lwDanger[100],
  ...borderlessTertiaryButtonStyles,
  '& .MuiButton-startIcon svg path': {
    fill: 'none',
    stroke: theme.palette.lwDanger[100],
  },
}));
